import React from 'react'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import { webViewPropertyOwnerShortTermAccommodationRentSchema } from './webViewPropertyOwnerShortTermAccommodationRentSchema'
import {
  SCREEN_ADDITIONAL_INFORMATION_CTA,
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_DETAILS,
  SCREEN_WEB_VIEW_AMENITIES,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import {
  isFurnished,
  petsAllowed,
  smokingAllowed,
  webViewParkingSpace,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import {
  livingSpaceField,
  roomField,
  startRentalDateWithToggleField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { CalendaredRentPriceTypeOption } from '../../../../../../@types/dropdown-types'
import {
  buildingTypeField,
  BuildingTypeTranslationKey,
} from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { priceToApiShortTermRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { getDefaultClasses } from '../../../../../../utils/style-utils'

import { WebViewMultimedia } from '../../../../../../components/pages/insertionPage/screens/multimediaNew/webView/WebViewMultimedia'
import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import { AdditionalInformationCta } from '../../../../../../components/pages/insertionPage/screens/objectDetailsCta/AdditionalInformationCta'
import { ObjectDetails } from '../../../../../../components/pages/insertionPage/screens/objectDetails/ObjectDetails'
import {
  floorNumberField,
  maxNumberOfPersons,
  maxRentalTime,
  minRentalTime,
  totalFloorsField,
} from '../../../../../../components/pages/insertionPage/screens/objectDetails/wrappers/objectDetailsFieldsWrapper'
import WebViewConstruction from '../../../../../../components/pages/insertionPage/screens/construction/webView/WebViewConstruction'
import WebViewAmenities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/WebViewAmenities'
import { AmenitiesLabel } from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/AmenitiesLabel'
import {
  createShortTermAccommodationRentEstateFeaturesForWebView,
  shortTermAccommodationRentBuildingTypeOptions,
} from '../common'
import WebViewCostsRent from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/webView/WebViewCostsRent'

const webViewPropertyOwnerShortTermAccommodationRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <WebViewMultimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        properties={[
          buildingTypeField(
            'shortTermLiving',
            shortTermAccommodationRentBuildingTypeOptions,
            getDefaultClasses('full'),
            BuildingTypeTranslationKey.SHORT_TERM_ACCOMMODATION
          ),
          livingSpaceField('full', undefined, true),
          roomField('one-whole margin-top-l', 'wizard.screen.basicInfo.room.label', true),
          startRentalDateWithToggleField('margin-top-l grid-item one-whole'),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => <WebViewCostsRent {...props} showRentPeriodSelector />,
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
  {
    screen: SCREENS[SCREEN_ADDITIONAL_INFORMATION_CTA],
    element: () => <AdditionalInformationCta screens={['objectDetails', 'construction', 'webViewAmenities']} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_DETAILS],
    element: (props) => (
      <ObjectDetails
        {...props}
        properties={[
          floorNumberField(getDefaultClasses('full')),
          totalFloorsField(getDefaultClasses('full'), undefined, 'wizard.screen.objectDetails.totalFloors.tooltip'),
          maxNumberOfPersons(getDefaultClasses('full')),
          minRentalTime(getDefaultClasses('full')),
          maxRentalTime(getDefaultClasses('full')),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <WebViewConstruction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_WEB_VIEW_AMENITIES],
    element: (props) => (
      <WebViewAmenities
        {...props}
        properties={[
          createShortTermAccommodationRentEstateFeaturesForWebView(<AmenitiesLabel />, 'grid-item one-whole'),
          isFurnished(),
          smokingAllowed('grid-item one-whole margin-top-l'),
          petsAllowed('grid-item one-whole margin-top-l', false, true),
          webViewParkingSpace('grid-item one-whole margin-top-xl'),
        ]}
      />
    ),
  },
]

export const webViewPropertyOwnerShortTermAccommodationRentFlow: Flow = {
  flowInformation: {
    isMobileFlow: true,
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.short_term_accommodation',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT,
    validationSchema: webViewPropertyOwnerShortTermAccommodationRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.SHORT_TERM_ACCOMMODATION,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiShortTermRent,
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    pricePeriodType: { value: CalendaredRentPriceTypeOption.DAY, label: 'Tag' },
  },
  flowScreens: webViewPropertyOwnerShortTermAccommodationRentFlowScreens,
}
